import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';


const ContactMethods = ({ onMethodSelect }) => {
  const contactMethods = [
    {
      id: 'project',
      title: 'Project Inquiry',
      description: 'Ready to start a new project or collaboration',
      icon: 'Briefcase',
      color: 'from-accent to-blue-400',
      action: 'Start Project Brief',
      details: 'Typical response: 4-6 hours'
    },
    {
      id: 'consultation',
      title: 'Free Consultation',
      description: 'Discuss your ideas and get expert advice',
      icon: 'MessageCircle',
      color: 'from-cosmic-energy to-orange-400',
      action: 'Book Consultation',
      details: '30-minute video call'
    },
    {
      id: 'collaboration',
      title: 'Collaboration',
      description: 'Partner with me on creative projects',
      icon: 'Users',
      color: 'from-purple-500 to-pink-500',
      action: 'Discuss Partnership',
      details: 'Long-term opportunities'
    },
    {
      id: 'speaking',
      title: 'Speaking & Workshops',
      description: 'Invite me to speak at your event or conduct workshops',
      icon: 'Mic',
      color: 'from-green-500 to-emerald-400',
      action: 'Send Invitation',
      details: 'Available globally'
    }
  ];

  const socialLinks = [
    {
      name: 'LinkedIn',
      icon: 'Linkedin',
      url: 'https://linkedin.com/in/portfolio-showcase',
      color: 'text-blue-400 hover:text-blue-300'
    },
    {
      name: 'Twitter',
      icon: 'Twitter',
      url: 'https://twitter.com/portfolio_3d',
      color: 'text-sky-400 hover:text-sky-300'
    },
    {
      name: 'GitHub',
      icon: 'Github',
      url: 'https://github.com/portfolio-showcase',
      color: 'text-gray-400 hover:text-gray-300'
    },
    {
      name: 'Dribbble',
      icon: 'Dribbble',
      url: 'https://dribbble.com/portfolio-showcase',
      color: 'text-pink-400 hover:text-pink-300'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Contact Methods Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {contactMethods?.map((method, index) => (
          <motion.div
            key={method?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-panel-light rounded-xl p-6 hover:bg-surface/20 transition-all duration-300 cursor-pointer group"
            onClick={() => onMethodSelect(method?.id)}
          >
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${method?.color} rounded-lg flex items-center justify-center cosmic-glow-hover`}>
                <Icon name={method?.icon} size={24} className="text-white" />
              </div>
              
              <div className="flex-1">
                <h3 className="font-headline text-lg text-foreground group-hover:text-accent transition-colors">
                  {method?.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground mt-1 mb-3">
                  {method?.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-body text-xs text-accent">
                    {method?.details}
                  </span>
                  <Icon name="ArrowRight" size={16} className="text-muted-foreground group-hover:text-accent transition-colors" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Quick Contact Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-panel rounded-xl p-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Zap" size={20} className="text-accent" />
          <h3 className="font-headline text-lg text-foreground">Quick Connect</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Icon name="Mail" size={16} className="text-muted-foreground" />
              <span className="font-body text-sm text-foreground">ak253.com@gmail.com</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="Phone" size={16} className="text-muted-foreground" />
              <span className="font-body text-sm text-foreground">+1 (555) 123-4567</span>
            </div>
            <div className="flex items-center space-x-3">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <span className="font-body text-sm text-foreground">Haryana, IN</span>
            </div>
          </div>
          
          <div>
            <h4 className="font-body font-medium text-foreground mb-3">Follow My Work</h4>
            <div className="flex space-x-3">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-lg bg-surface/50 flex items-center justify-center transition-all duration-200 hover:bg-surface ${social?.color}`}
                >
                  <Icon name={social?.icon} size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
      {/* Availability Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-panel-light rounded-xl p-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
            <span className="font-body text-sm text-foreground">Currently Available</span>
          </div>
          <div className="text-right">
            <p className="font-body text-xs text-muted-foreground">Next availability</p>
            <p className="font-body text-sm text-accent">January 2025</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactMethods;